import { useState, useRef, useEffect, useCallback } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';

import { useBusinessAccount } from '../business-dashboard/useBusinessAccount';

/* ── Fonts ── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";

if (!document.getElementById("__notif_fonts")) {
  const l = document.createElement("link");
  l.id = "__notif_fonts";
  l.rel = "stylesheet";
  l.href = FONTS_URL;
  document.head.appendChild(l);
}

/* ── Design tokens ── */
const T = {
  green:      "#1e9254",
  greenDark:  "#155c36",
  greenMid:   "#1b7a47",
  greenTint:  "#eef8f3",
  greenBorder:"#a7d9bc",

  bg:         "#F7F8FA",
  white:      "#FFFFFF",
  border:     "#E5E7EB",
  borderFaint:"#F0F2F5",

  text:       "#0f1117",
  textMid:    "#2d3549",
  sub:        "#4a5268",
  faint:      "#8a93a8",
  ghost:      "#b0b8cc",

  hoverBg:    "#F3F4F6",

  serif:      "'Instrument Serif', Georgia, serif",
  sans:       "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ════════════════════════════════════════════
   TIME HELPERS
════════════════════════════════════════════ */
const NOW = Date.now();
const mins = (m) => NOW - m * 60 * 1000;
const hrs  = (h) => NOW - h * 60 * 60 * 1000;
const days = (d) => NOW - d * 24 * 60 * 60 * 1000;

function humanizeTime(timestamp) {
  const diffMs  = NOW - timestamp;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr  = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1)   return "Just now";
  if (diffMin < 60)  return `${diffMin}m ago`;
  if (diffHr  < 24)  return `${diffHr}h ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7)   return `${diffDay}d ago`;
  return new Date(timestamp).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}

function getTimeGroup(timestamp) {
  const diffDay = Math.floor((NOW - timestamp) / 86400000);
  if (diffDay < 1) return "Today";
  if (diffDay < 2) return "Yesterday";
  return "Earlier";
}

/* ════════════════════════════════════════════
   MOCK DATA
════════════════════════════════════════════ */
const initialNotifications = [
  {
    id: 1,
    avatar: "PL", avatarBg: "#FEF9C3", avatarColor: "#92400E",
    primaryText: "Peter L. requested Garden Maintenance",
    secondaryText: "Garden Maintenance · Apr 2, 8:00 AM",
    type: "Booking", subtype: "BookingRequest",
    timestamp: hrs(4), read: false, unread: true,
    actionable: true,
    actions: [
      { label: "Accept",  style: "primary", actionKey: "accepted" },
      { label: "Decline", style: "secondary", actionKey: "declined" },
    ],
    status: "pending", priorityScore: 3,
  },
  {
    id: 2,
    avatar: "AM", avatarBg: "#EDE9FE", avatarColor: "#6D28D9",
    primaryText: "Aisha M. sent you a message",
    secondaryText: "Plumbing Fix · Today, 2:00 PM",
    type: "Message", subtype: "NewMessage",
    timestamp: hrs(3), read: false, unread: true,
    actionable: true,
    actions: [{ label: "Reply", style: "primary", nav: "/messages" }],
    status: "pending", priorityScore: 3,
  },
  {
    id: 3,
    avatar: "MW", avatarBg: "#DCFCE7", avatarColor: "#166534",
    primaryText: "Mary W. accepted your booking",
    secondaryText: "Deep House Cleaning · Today, 9:00 AM",
    type: "Booking", subtype: "BookingAccepted",
    timestamp: mins(30), read: false, unread: true,
    actionable: true,
    actions: [{ label: "View booking", style: "primary", nav: "/bookings" }],
    status: "pending", priorityScore: 2,
  },
  {
    id: 4,
    avatar: "JK", avatarBg: "#DBEAFE", avatarColor: "#1D4ED8",
    primaryText: "James K. completed your job",
    secondaryText: "Laundry & Ironing · Completed at 11:00 AM",
    type: "Review", subtype: "JobCompleted",
    timestamp: hrs(1), read: false, unread: true,
    actionable: true,
    actions: [{ label: "Rate now", style: "primary", nav: "/bookings" }],
    status: "pending", priorityScore: 2,
  },
  {
    id: 5,
    avatar: "GO", avatarBg: "#DCFCE7", avatarColor: "#166534",
    primaryText: "Grace O. sent your payout",
    secondaryText: "Painting Job · KSh 4,800 sent to M-Pesa",
    type: "Payout", subtype: "PayoutSent",
    timestamp: days(1), read: true, unread: false,
    actionable: true,
    actions: [{ label: "View earnings", style: "primary", nav: "/earnings" }],
    status: "pending", priorityScore: 2,
  },
  {
    id: 6,
    avatar: "BN", avatarBg: "#DBEAFE", avatarColor: "#1D4ED8",
    primaryText: "Brian N. sent you a message",
    secondaryText: "AC Repair · Yesterday, 3:00 PM",
    type: "Message", subtype: "NewMessage",
    timestamp: days(1), read: true, unread: false,
    actionable: true,
    actions: [{ label: "Reply", style: "primary", nav: "/messages" }],
    status: "pending", priorityScore: 3,
  },
  {
    id: 7,
    avatar: "TN", avatarBg: "#FEF3C7", avatarColor: "#92400E",
    primaryText: "Tom N. left you a 5-star review",
    secondaryText: "Electrical Repair · Yesterday, 10:00 AM",
    type: "Review", subtype: "ReviewReceived",
    timestamp: days(1), read: true, unread: false,
    actionable: true,
    actions: [{ label: "View review", style: "primary", nav: "/bookings" }],
    status: "pending", priorityScore: 1,
  },
  {
    id: 8,
    avatar: "SN", avatarBg: "#FCE7F3", avatarColor: "#9D174D",
    primaryText: "Your profile is 80% complete",
    secondaryText: "Add a profile photo to attract more clients",
    type: "System", subtype: "ProfileReminder",
    timestamp: days(1), read: true, unread: false,
    actionable: true,
    actions: [{ label: "Complete profile", style: "primary", nav: "/account" }],
    status: "pending", priorityScore: 1,
  },
  {
    id: 9,
    avatar: "KO", avatarBg: "#DCFCE7", avatarColor: "#166534",
    primaryText: "Kelvin O. left a 4-star review",
    secondaryText: "Deep Cleaning · 3 days ago",
    type: "Review", subtype: "ReviewReceived",
    timestamp: days(3), read: true, unread: false,
    actionable: true,
    actions: [{ label: "View review", style: "primary", nav: "/bookings" }],
    status: "pending", priorityScore: 1,
  },
];

/* ════════════════════════════════════════════
   DB ROW MAPPER
════════════════════════════════════════════ */
const mapNotificationRow = (row) => {
  const getDefaultAvatar    = (t) => ({ Booking:"BK", Message:"MG", Review:"RV", Payout:"PY" }[t] || "SY");
  const getDefaultAvatarBg  = (t) => ({ Booking:"#FEF9C3", Message:"#EDE9FE", Review:"#DCFCE7", Payout:"#DBEAFE" }[t] || "#F3F4F6");
  const getDefaultAvatarColor=(t) => ({ Booking:"#92400E", Message:"#6D28D9", Review:"#166534", Payout:"#1D4ED8" }[t] || "#6B7280");

  let actions = [];
  if (row.type === "Booking" && row.subtype === "BookingRequest") {
    actions = [{ label:"Accept", style:"primary", actionKey:"accepted" }, { label:"Decline", style:"secondary", actionKey:"declined" }];
  } else if (row.type === "Message") {
    actions = [{ label:"Reply", style:"primary", nav:"/messages" }];
  } else if (row.type === "Review") {
    actions = [{ label: row.subtype === "JobCompleted" ? "Rate now" : "View review", style:"primary", nav:"/bookings" }];
  } else if (row.type === "Payout") {
    actions = [{ label:"View earnings", style:"primary", nav:"/earnings" }];
  } else if (row.type === "System") {
  if (row.subtype === "ApplicationApproved") {
    actions = [{ label: "Go to dashboard", style: "primary", nav: "/pro/dashboard" }];
  } else if (row.subtype === "ApplicationRejected") {
    actions = [{ label: "View details", style: "primary", nav: "/pro-signup/rejected" }];
  } else {
    actions = [{ label: "Complete profile", style: "primary", nav: "/account" }];
  }
}

  return {
    id: row.id,
    avatar: row.avatar || getDefaultAvatar(row.type),
    avatarBg: row.avatarBg || getDefaultAvatarBg(row.type),
    avatarColor: row.avatarColor || getDefaultAvatarColor(row.type),
    primaryText: row.primaryText || row.title || "Notification",
    secondaryText: row.secondaryText || row.body || "",
    type: row.type || "System",
    subtype: row.subtype || "General",
    timestamp: row.created_at ? new Date(row.created_at).getTime() : NOW,
    read: row.read || false,
    unread: !(row.read || false),
    actionable: actions.length > 0,
    actions,
    status: row.status || "pending",
    priorityScore: row.priorityScore || 2,
    linkTo: row.link_to || null,
  };
};

/* ════════════════════════════════════════════
   FEED LOGIC
════════════════════════════════════════════ */
function isDemoted(n) {
  return (NOW - n.timestamp) / 86400000 >= 2 && n.read;
}
function isHiddenByDefault(n, filter) {
  if (filter !== "All") return false;
  return n.subtype === "ProfileReminder" && n.read && (NOW - n.timestamp) / 86400000 > 3;
}

const COLLAPSIBLE_SUBTYPES = ["NewMessage", "BookingRequest"];

function buildSortedFeed(notifications, filter, expandedGroups) {
  let visible = notifications.filter(n => {
    if (isHiddenByDefault(n, filter)) return false;
    if (filter === "All")    return true;
    if (filter === "Unread") return n.unread;
    return n.type === filter;
  });

  visible = [...visible].sort((a, b) =>
    b.priorityScore !== a.priorityScore ? b.priorityScore - a.priorityScore : b.timestamp - a.timestamp
  );

  const importantItems = visible.filter(n => n.priorityScore === 3 && n.status === "pending" && !isDemoted(n)).slice(0, 3);
  const importantIds = new Set(importantItems.map(n => n.id));
  const remaining = visible.filter(n => !importantIds.has(n.id));

  const timeGroupOrder = ["Today", "Yesterday", "Earlier"];
  const byGroup = {};
  remaining.forEach(n => {
    const g = getTimeGroup(n.timestamp);
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(n);
  });

  const buildGroupFeed = (items) => {
    const result = [];
    const handled = new Set();
    items.forEach(n => {
      if (handled.has(n.id)) return;
      if (COLLAPSIBLE_SUBTYPES.includes(n.subtype)) {
        const siblings = items.filter(x => x.subtype === n.subtype && !handled.has(x.id));
        if (siblings.length >= 2) {
          siblings.forEach(s => handled.add(s.id));
          const collapseKey = `${n.subtype}-${getTimeGroup(n.timestamp)}`;
          result.push({ _isCluster: true, collapseKey, subtype: n.subtype, count: siblings.length, items: siblings, isExpanded: expandedGroups.has(collapseKey), priorityScore: Math.max(...siblings.map(s => s.priorityScore)) });
          return;
        }
      }
      handled.add(n.id);
      result.push({ _isCluster: false, ...n });
    });
    return result;
  };

  const sections = timeGroupOrder.filter(g => byGroup[g]?.length).map(g => ({ label: g, items: buildGroupFeed(byGroup[g]) }));
  return { important: importantItems, sections };
}

const pulsedIds = new Set();

/* ════════════════════════════════════════════
   SIDEBAR — same structure as MessagingPage
════════════════════════════════════════════ */
function useBtn() {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const bind = {
    onMouseEnter: () => setH(true),
    onMouseLeave: () => { setH(false); setP(false); },
    onMouseDown:  () => setP(true),
    onMouseUp:    () => setP(false),
  };
  return { h, p, bind };
}

function BrandMark({ size = 26 }) {
  const w = size * (87 / 52);
  return (
    <svg width={w} height={size} viewBox="0 0 87 52" aria-hidden="true" focusable="false"
      style={{ overflow: "visible", display: "block" }}>
      <circle cx="26" cy="26" r="26" fill="#1e9254" />
      <circle cx="61" cy="26" r="26" fill="#15703a" />
    </svg>
  );
}

function NavIconBtn({ icon, active, onClick, badge }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();

  const bg = active
    ? "rgba(30,146,84,0.10)"
    : p ? "#ECEEF1"
    : h ? "#F3F4F6"
    : "transparent";

  const iconStroke = active ? T.green : p ? T.sub : h ? "#9CA3AF" : "#B0B7C3";

  return (
    <button
      {...bind}
      onClick={onClick}
      style={{
        width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 10,
        border: "none", outline: "none",
        background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        transition: T.tx,
        transform: p && !active ? "scale(0.92)" : "scale(1)",
        position: "relative",
      }}
    >
      <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24"
        fill="none" stroke={iconStroke} strokeWidth={active ? 1.8 : 1.5}
        strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(icon)
          ? icon.map((d, i) => <path key={i} d={d} />)
          : <path d={icon} />}
      </svg>
      {badge > 0 && (
        <span style={{
          position: "absolute", top: 6, right: 6,
          width: 7, height: 7, borderRadius: "50%",
          background: T.green, border: "1.5px solid #F7F8FA",
        }} />
      )}
    </button>
  );
}

function IconSidebar({ navigate }) {
  const { logoutUser, user } = useAuth();
  const { isMobile } = useBreakpoint();
  const logo = useBtn();
  const sidebarWidth = isMobile ? 64 : 72;
  const isPro = user?.role === 'pro';

  if (isMobile) return null;

  // No "notifications" entry — this page IS notifications, so it doesn't
  // link to itself. Pros don't get Home or Bookings; customers don't get Earnings.
  const items = isPro
    ? [
        { id: "earnings", icon: ["M1 4h22v16H1z", "M1 10h22"], path: "/earnings" },
        { id: "messages", icon: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"], path: "/messages" },
        { id: "help",     icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
      ]
    : [
        { id: "home",     icon: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"], path: "/" },
        { id: "bookings", icon: ["M8 2v4", "M16 2v4", "M3 10h18", "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"], path: "/bookings" },
        { id: "messages", icon: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"], path: "/messages" },
        { id: "help",     icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
      ];

  return (
    <div style={{
      width: sidebarWidth, flexShrink: 0,
      background: T.bg,
      borderRight: `1px solid ${T.border}`,
      display: "flex", flexDirection: "column", alignItems: "center",
      position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
    }}>
      {/* Logo — icon only, no wordmark, no divider */}
      <div
        {...logo.bind}
        onClick={() => navigate(isPro ? "/pro/dashboard" : "/")}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && navigate(isPro ? "/pro/dashboard" : "/")}
        style={{
          height: 60, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          background: logo.p ? "#ECEEF1" : logo.h ? "#F3F4F6" : "transparent",
          transition: T.tx, outline: "none",
          padding: "0 8px",
        }}
      >
        <BrandMark size={26} />
      </div>

      {/* Nav icons */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", gap: 2,
        padding: "6px 0",
      }}>
        {items.map(item => (
          <NavIconBtn
            key={item.id}
            icon={item.icon}
            active={false}
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>

      <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <NavIconBtn
          icon="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
          active={false}
          onClick={() => navigate(isPro ? "/pro/dashboard" : "/account")}
        />
        {/* Logout reads as a distinct, deliberate action: red on hover. */}
        <button
          onClick={() => { logoutUser?.(); navigate("/"); }}
          title="Log out"
          style={{
            width: 40, height: 40, borderRadius: 10,
            border: "none", background: "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: T.tx, color: T.faint,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#C0392B"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.faint; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

function MobileNavItem({ icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, border: "none", background: "transparent",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: 3, padding: "8px 0 6px",
      cursor: "pointer", outline: "none",
    }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
        stroke="#AEB4C2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(icon) ? icon.map((d, i) => <path key={i} d={d} />) : <path d={icon} />}
      </svg>
      <span style={{ fontSize: 10, fontWeight: 500, color: "#AEB4C2", fontFamily: T.sans }}>{label}</span>
    </button>
  );
}

function MobileBottomNav({ navigate }) {
  const { user } = useAuth();
  const isPro = user?.role === 'pro';

  const items = isPro
    ? [
        { id: "earnings", label: "Earnings", icon: ["M1 4h22v16H1z", "M1 10h22"], path: "/earnings" },
        { id: "messages", label: "Messages", icon: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"], path: "/messages" },
        { id: "help",     label: "Help",     icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
        { id: "account",  label: "Profile",  icon: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", path: "/pro/dashboard" },
      ]
    : [
        { id: "home",     label: "Home",     icon: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"], path: "/" },
        { id: "bookings", label: "Bookings", icon: ["M8 2v4", "M16 2v4", "M3 10h18", "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"], path: "/bookings" },
        { id: "messages", label: "Messages", icon: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"], path: "/messages" },
        { id: "help",     label: "Help",     icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
        { id: "account",  label: "Account",  icon: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", path: "/account" },
      ];

  return (
    <div style={{
      flexShrink: 0, background: T.white,
      borderTop: `1px solid ${T.border}`,
      display: "flex", alignItems: "stretch",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>
      {items.map(item => (
        <MobileNavItem key={item.id} icon={item.icon} label={item.label} onClick={() => navigate(item.path)} />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════
   RIGHT PANEL — editorial trust cards
════════════════════════════════════════════ */

// Each card has an image, a label, heading, short desc, and a link
const EDITORIAL_CARDS = [
  {
    img: "/images/jirani-guarantee.png",
    eyebrow: "Your protection",
    heading: "The JiraniApp Guarantee",
    desc: "Every booking is backed by our guarantee. If something goes wrong, we make it right.",
    link: "/guarantee",
    linkLabel: "Learn how we protect you",
  },
  {
    img: "/images/verified-pros.png",
    eyebrow: "Who you're booking",
    heading: "Verified JiraniPros only",
    desc: "Every pro on the platform is identity-verified and reviewed by real clients.",
    link: "/find-a-jiranipro",
    linkLabel: "Browse verified pros",
  },
  {
    img: "/images/mpesa-safe.png",
    eyebrow: "Safe payments",
    heading: "Pay securely with M-Pesa",
    desc: "Payment is held safely and only released once your job is done and confirmed.",
    link: "/how-it-works",
    linkLabel: "How payments work",
  },
];

function EditorialCard({ card, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16,
        overflow: "hidden",
        border: `1.5px solid ${hovered ? T.greenBorder : T.border}`,
        background: T.white,
        transition: T.tx,
        cursor: "pointer",
        boxShadow: hovered ? "0 8px 28px rgba(30,146,84,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onClick={() => navigate(card.link)}
    >
      {/* Image with edge fades */}
      <div style={{ position: "relative", width: "100%", height: 130, overflow: "hidden", background: "#E5E7EB" }}>
        <img
          src={card.img}
          alt={card.heading}
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            transition: "transform 0.45s cubic-bezier(.4,0,.2,1)",
            transform: hovered ? "scale(1.04)" : "scale(1)",
            display: "block",
          }}
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
        {/* Bottom fade to white */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 56,
          background: "linear-gradient(to top, rgba(255,255,255,0.95) 0%, transparent 100%)",
          pointerEvents: "none",
        }} />
        {/* Left edge fade */}
        <div style={{
          position: "absolute", top: 0, left: 0, bottom: 0, width: 28,
          background: "linear-gradient(to right, rgba(255,255,255,0.30) 0%, transparent 100%)",
          pointerEvents: "none",
        }} />
        {/* Right edge fade */}
        <div style={{
          position: "absolute", top: 0, right: 0, bottom: 0, width: 28,
          background: "linear-gradient(to left, rgba(255,255,255,0.30) 0%, transparent 100%)",
          pointerEvents: "none",
        }} />
        {/* Top edge fade */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 20,
          background: "linear-gradient(to bottom, rgba(255,255,255,0.18) 0%, transparent 100%)",
          pointerEvents: "none",
        }} />
      </div>

      {/* Content */}
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{
          fontSize: 10, fontWeight: 600, letterSpacing: "0.09em",
          textTransform: "uppercase", color: T.green,
          fontFamily: T.sans, marginBottom: 5,
        }}>
          {card.eyebrow}
        </div>
        <div style={{
          fontFamily: T.serif,
          fontSize: 16, fontWeight: 400, color: T.text,
          lineHeight: 1.3, letterSpacing: "-0.02em", marginBottom: 6,
          fontStyle: "italic",
        }}>
          {card.heading}
        </div>
        <p style={{
          fontSize: 12.5, color: T.sub, lineHeight: 1.65,
          margin: "0 0 10px", fontFamily: T.sans,
        }}>
          {card.desc}
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 12, fontWeight: 600, color: T.green, fontFamily: T.sans,
          transition: T.tx,
          transform: hovered ? "translateX(2px)" : "translateX(0)",
        }}>
          {card.linkLabel}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function RightPanel({ navigate }) {
  const { isMobile } = useBreakpoint();

  return (
    <aside style={{
      width: "100%", flexShrink: 0,
      padding: isMobile ? "0 16px 40px" : "32px 20px 32px 0",
      display: "flex", flexDirection: "column", gap: 24,
    }}>
      {/* Section heading */}
      <div>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.09em",
          textTransform: "uppercase", color: T.faint,
          fontFamily: T.sans, margin: "0 0 10px",
        }}>
          From JiraniApp
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {EDITORIAL_CARDS.map((card, i) => (
            <EditorialCard key={i} card={card} navigate={navigate} />
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div style={{
        padding: "18px 16px",
        borderRadius: 14,
        border: `1px solid ${T.border}`,
        background: T.white,
      }}>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.09em",
          textTransform: "uppercase", color: T.faint,
          fontFamily: T.sans, margin: "0 0 12px",
        }}>
          Useful links
        </p>
        {[
          { label: "How JiraniApp works", path: "/how-it-works" },
          { label: "Trust & Safety",       path: "/trust-and-safety" },
          { label: "Help & Support",        path: "/help" },
          { label: "Community Hub",         path: "/community-hub" },
          { label: "Browse by location",    path: "/browse-by-location" },
        ].map((link, i) => (
          <QuickLink key={i} link={link} navigate={navigate} />
        ))}
      </div>
    </aside>
  );
}

function QuickLink({ link, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(link.path)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #F3F4F6",
        cursor: "pointer",
        transition: T.tx,
      }}
    >
      <span style={{
        fontSize: 13, color: hovered ? T.green : T.textMid,
        fontFamily: T.sans, fontWeight: 400,
        transition: T.tx,
      }}>
        {link.label}
      </span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={hovered ? T.green : T.ghost}
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transition: T.tx, transform: hovered ? "translateX(2px)" : "none" }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </div>
  );
}

/* ════════════════════════════════════════════
   NOTIFICATION ROW
════════════════════════════════════════════ */
function ActionButton({ action, loading, onPress }) {
  const { isMobile } = useBreakpoint();
  const isPrimary = action.style === "primary";
  return (
    <button
      disabled={loading}
      onClick={e => { e.stopPropagation(); onPress(action); }}
      style={{
        height: 28, padding: "0 12px", borderRadius: 8,
        fontSize: 12, fontWeight: 600, fontFamily: T.sans,
        cursor: loading ? "default" : "pointer",
        border: isPrimary ? "none" : `1px solid ${T.border}`,
        background: isPrimary ? T.green : "transparent",
        color: isPrimary ? "#FFFFFF" : T.textMid,
        opacity: loading ? 0.6 : 1,
        display: "flex", alignItems: "center", gap: 5,
        transition: T.tx,
      }}
      onMouseEnter={e => { if (!loading && isPrimary) e.currentTarget.style.background = T.greenMid; if (!loading && !isPrimary) e.currentTarget.style.background = T.hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.background = isPrimary ? T.green : "transparent"; }}
    >
      {loading && (
        <span style={{ width: 8, height: 8, borderRadius: "50%", border: "1.5px solid currentColor", borderTopColor: "transparent", display: "inline-block", animation: "spin 600ms linear infinite", flexShrink: 0 }} />
      )}
      {action.label}
    </button>
  );
}

function getContextLabel(n) {
  if (n.status === "accepted" || n.status === "declined") return null;
  if (n.priorityScore === 3 && n.subtype === "BookingRequest")  return "Action needed";
  if (n.priorityScore === 3 && n.subtype === "NewMessage")      return "Awaiting reply";
  if (n.priorityScore === 2 && n.subtype === "JobCompleted")    return "Rate to close";
  if (n.priorityScore === 2 && n.subtype === "BookingAccepted") return "Confirmed";
  return null;
}

function NotifRow({ n, staggerIdx, navigate, onAction, onRowClick }) {
  const [loadingAction, setLoadingAction] = useState(null);
  const { isMobile } = useBreakpoint();

  const shouldPulse = n.unread && !pulsedIds.has(n.id);
  if (shouldPulse) pulsedIds.add(n.id);

  const isHighPriority = n.priorityScore === 3;
  const isResolved     = n.status === "accepted" || n.status === "declined";
  const showActions    = n.actionable && n.actions?.length > 0 && !isResolved;
  const demoted        = isDemoted(n);
  const contextLabel   = getContextLabel(n);

  const handleAction = (action) => {
    if (action.nav) { onAction(n.id, null); navigate(action.nav); return; }
    if (action.actionKey) {
      setLoadingAction(action.actionKey);
      setTimeout(() => { setLoadingAction(null); onAction(n.id, action.actionKey); }, 600);
    }
  };

  const handleClick = () => {
    onRowClick(n.id);
    if (!showActions && n.linkTo) navigate(n.linkTo);
  };

  return (
    <div
      onClick={handleClick}
      className={`notif-row${shouldPulse ? " unread-pulse" : ""}`}
      style={{
        animationDelay: `${staggerIdx * 18}ms`,
        display: "flex", alignItems: "flex-start", gap: 12,
        padding: "14px 16px",
        background: n.unread
          ? (isHighPriority ? "#F0FAF4" : "#F7FBF8")
          : T.white,
        borderLeft: `3px solid ${n.unread ? T.green : "transparent"}`,
        borderBottom: `1px solid ${T.borderFaint}`,
        opacity: demoted ? 0.72 : 1,
        cursor: (!showActions && n.linkTo) ? "pointer" : "default",
        transition: "background 140ms ease, border-left-color 140ms ease",
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 38, height: 38, borderRadius: "50%",
        background: n.avatarBg, color: n.avatarColor,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
        fontFamily: T.sans, position: "relative",
        border: `1.5px solid rgba(0,0,0,0.06)`,
      }}>
        {n.avatar}
        {n.unread && (
          <span style={{
            position: "absolute", top: -2, right: -2,
            width: 9, height: 9, borderRadius: "50%",
            background: T.green, border: "2px solid #FFFFFF",
          }} />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{
              fontSize: 13.5,
              fontWeight: isHighPriority ? 600 : 500,
              color: T.text,
              lineHeight: 1.45,
              fontFamily: T.sans,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {n.primaryText}
            </span>
            {contextLabel && (
              <span style={{
                marginLeft: 6,
                fontSize: 11, color: T.green, fontWeight: 600,
                fontFamily: T.sans,
              }}>
                · {contextLabel}
              </span>
            )}
          </div>
          <span style={{
            fontSize: 11.5, color: T.ghost, whiteSpace: "nowrap",
            flexShrink: 0, fontFamily: T.sans, marginTop: 1,
          }}>
            {humanizeTime(n.timestamp)}
          </span>
        </div>

        <div style={{
          fontSize: 12.5, color: T.sub, marginTop: 3,
          lineHeight: 1.55, fontFamily: T.sans,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {n.secondaryText}
        </div>

        {isResolved ? (
          <div style={{
            marginTop: 8, fontSize: 12, fontWeight: 600,
            color: n.status === "accepted" ? T.green : T.faint,
            fontFamily: T.sans,
          }}>
            {n.status === "accepted" ? "Accepted" : "Declined"}
          </div>
        ) : showActions && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {n.actions.map(action => (
              <ActionButton
                key={action.label}
                action={action}
                loading={loadingAction === action.actionKey}
                onPress={handleAction}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClusterRow({ cluster, staggerIdx, navigate, onAction, onRowClick, onToggle }) {
  const { isMobile } = useBreakpoint();
  const subtypeLabels = { NewMessage: "new messages", BookingRequest: "booking requests" };
  const label = subtypeLabels[cluster.subtype] || "notifications";

  return (
    <div>
      <div
        className="notif-row"
        onClick={() => onToggle(cluster.collapseKey)}
        style={{
          animationDelay: `${staggerIdx * 18}ms`,
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 16px",
          background: T.white,
          borderLeft: "3px solid transparent",
          borderBottom: cluster.isExpanded ? "none" : `1px solid ${T.borderFaint}`,
          cursor: "pointer",
        }}
      >
        <div style={{ position: "relative", width: 38, height: 38, flexShrink: 0 }}>
          {cluster.items.slice(0, 2).map((av, i) => (
            <div key={i} style={{
              position: "absolute", left: i * 9,
              width: 30, height: 30, borderRadius: "50%",
              background: av.avatarBg, color: av.avatarColor,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9, fontWeight: 700, border: "2px solid #F7F8FA",
              zIndex: 2 - i, fontFamily: T.sans,
            }}>{av.avatar}</div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: T.text, fontFamily: T.sans }}>
            {cluster.count} {label}
          </span>
          <div style={{ fontSize: 12.5, color: T.sub, marginTop: 2, fontFamily: T.sans }}>
            {cluster.isExpanded ? "Tap to collapse" : "Tap to see all"}
          </div>
        </div>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.ghost}
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: cluster.isExpanded ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {cluster.isExpanded && (
        <div>
          {cluster.items.map((n, i) => (
            <div key={n.id} style={{ paddingLeft: 16 }}>
              <NotifRow n={n} staggerIdx={i} navigate={navigate} onAction={onAction} onRowClick={onRowClick} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupLabel({ label, first }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: T.faint,
      textTransform: "uppercase", letterSpacing: "0.09em",
      marginTop: first ? 0 : 28, marginBottom: 4,
      padding: "6px 16px 4px",
      fontFamily: T.sans,
    }}>
      {label}
    </div>
  );
}

/* ════════════════════════════════════════════
   FILTER PILL
════════════════════════════════════════════ */
const FILTERS = ["All", "Unread", "Booking", "Review", "Message", "Payout"];

const EMPTY_MESSAGES = {
  All:     "No new notifications right now",
  Unread:  "You're all caught up",
  Booking: "No booking activity right now",
  Review:  "No review activity yet",
  Message: "No new messages",
  Payout:  "No payout activity",
};

/* ════════════════════════════════════════════
   PAGE
════════════════════════════════════════════ */
export default function NotificationsPage() {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { isLoggedIn, user } = useAuth();
  const { isMobile } = useBreakpoint();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter]               = useState("All");
  const [filterKey, setFilterKey]         = useState(0);
  const [feedVisible, setFeedVisible]     = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const filterTimerRef = useRef(null);
  const channelRef     = useRef(null);

  let businessAccount = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useBusinessAccount();
    businessAccount = ctx?.account ?? null;
  } catch {
    businessAccount = null;
  }

  const recipientId = businessAccount?.id ?? user?.id ?? null;

  useEffect(() => {
    if (!isLoggedIn) openLogin();
  }, [isLoggedIn, navigate]);

  const fetchNotifications = async (id, isBusiness) => {
    try {
      const column = isBusiness ? "recipient_id" : "user_id";
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq(column, id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      if (data && data.length > 0) return data.map(mapNotificationRow);
      return [];
    } catch {
      return isBusiness ? [] : initialNotifications;
    }
  };

  useEffect(() => {
    if (recipientId) fetchNotifications(recipientId, !!businessAccount?.id).then(setNotifications);
  }, [recipientId, businessAccount?.id]);

  useEffect(() => {
    if (!recipientId) return;
    const column = businessAccount?.id ? "recipient_id" : "user_id";
    channelRef.current = supabase
      .channel(`notifications-${recipientId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `${column}=eq.${recipientId}` }, payload => {
        setNotifications(prev => [mapNotificationRow(payload.new), ...prev]);
      })
      .subscribe();
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [recipientId, businessAccount?.id]);

  const markRead = (id) =>
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true, unread: false } : n));

  const markAllRead = () =>
    setNotifications(prev => prev.map(n => ({ ...n, read: true, unread: false })));

  const handleAction = async (id, actionKey) => {
    setNotifications(prev => prev.map(n =>
      n.id !== id ? n : { ...n, read: true, unread: false, status: actionKey ?? n.status }
    ));
    try {
      await supabase.from("notifications").update(actionKey ? { status: actionKey, read: true } : { read: true }).eq("id", id);
    } catch {}
  };

  const handleRowClick = (id) => {
    markRead(id);
    supabase.from("notifications").update({ read: true }).eq("id", id).catch(() => {});
  };

  const toggleCluster = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleFilterChange = (f) => {
    if (f === filter) return;
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    setFeedVisible(false);
    filterTimerRef.current = setTimeout(() => {
      setFilter(f);
      setFilterKey(k => k + 1);
      requestAnimationFrame(() => setFeedVisible(true));
    }, 100);
  };

  const { important, sections } = buildSortedFeed(notifications, filter, expandedGroups);
  const hasContent = important.length > 0 || sections.some(s => s.items.length > 0);
  const unreadCount = notifications.filter(n => n.unread).length;

  let staggerCounter = 0;
  const sidebarWidth = isMobile ? 64 : 72;

  return (
    <div style={{
      fontFamily: T.sans,
      background: T.bg,
      minHeight: "100vh",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
    }}>
      <style>{`
        @keyframes unread-pulse {
          0%   { opacity: 1; }
          40%  { opacity: 0.65; }
          100% { opacity: 1; }
        }
        .unread-pulse { animation: unread-pulse 1.2s ease 1 both; }

        @keyframes row-enter {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .notif-row { animation: row-enter 160ms cubic-bezier(.4,0,.2,1) both; }
        .notif-row:hover { background: #F3F4F6 !important; }

        @keyframes spin { to { transform: rotate(360deg); } }

        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 99px; }
      `}</style>

      {/* Sidebar — desktop only; IconSidebar returns null on mobile */}
      <IconSidebar navigate={navigate} />

      {/* Main layout */}
      <div style={{
        marginLeft: isMobile ? 0 : sidebarWidth,
        flex: 1,
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
      }}>
        {/* Centre column + right panel wrapper */}
        <div style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 1040,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 0,
          padding: isMobile ? "0" : "0 24px",
          alignItems: "flex-start",
        }}>

          {/* ── CENTRE COLUMN ── */}
          <div style={{
            flex: 1,
            minWidth: 0,
            paddingTop: isMobile ? 20 : 32,
            paddingBottom: isMobile ? 40 : 60,
            paddingLeft: isMobile ? 0 : 0,
            paddingRight: isMobile ? 0 : 32,
          }}>
            {/* ── PAGE HEADER ── */}
            <div style={{ padding: isMobile ? "0 16px 24px" : "0 0 28px" }}>
              {/* Eyebrow */}
              <p style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.09em",
                textTransform: "uppercase", color: T.green,
                fontFamily: T.sans, margin: "0 0 8px",
              }}>
                Your activity
              </p>
              {/* Serif headline */}
              <h1 style={{
                fontFamily: T.serif,
                fontSize: isMobile ? 28 : 34,
                fontWeight: 400,
                color: T.text,
                lineHeight: 1.12,
                letterSpacing: "-0.03em",
                margin: "0 0 6px",
              }}>
                {unreadCount > 0
                  ? <>You have <em style={{ fontStyle: "italic", color: T.green }}>{unreadCount} new</em></>
                  : <>You're all <em style={{ fontStyle: "italic", color: T.green }}>caught up</em></>}
              </h1>
              <p style={{
                fontSize: 14, color: T.sub, fontFamily: T.sans,
                margin: "0 0 20px", lineHeight: 1.6,
              }}>
                {unreadCount > 0
                  ? `${unreadCount} notification${unreadCount > 1 ? "s" : ""} waiting for your attention`
                  : "Nothing new since your last visit"}
              </p>

              {/* Header actions row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                {/* Filter pills */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {FILTERS.map(f => (
                    <button key={f} onClick={() => handleFilterChange(f)} style={{
                      height: 30, padding: "0 13px", borderRadius: 999,
                      border: filter === f ? "none" : `1px solid ${T.border}`,
                      fontSize: 12.5, fontWeight: filter === f ? 600 : 400,
                      fontFamily: T.sans, cursor: "pointer",
                      background: filter === f ? T.text : T.white,
                      color: filter === f ? "#FFFFFF" : T.sub,
                      transition: T.tx,
                    }}>
                      {f}
                      {f === "Unread" && unreadCount > 0 && (
                        <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700 }}>({unreadCount})</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Mark all read */}
                <button onClick={markAllRead} style={{
                  height: 30, padding: "0 14px", borderRadius: 8,
                  border: `1px solid ${T.border}`, fontSize: 12.5, fontWeight: 500,
                  fontFamily: T.sans, cursor: "pointer",
                  background: T.white, color: T.sub,
                  transition: T.tx, flexShrink: 0,
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.greenBorder; e.currentTarget.style.color = T.green; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.sub; }}
                >
                  Mark all as read
                </button>
              </div>
            </div>

            {/* ── FEED CARD ── */}
            <div style={{
              borderRadius: isMobile ? 0 : 16,
              border: isMobile ? "none" : `1.5px solid ${T.border}`,
              background: T.white,
              overflow: "hidden",
              boxShadow: isMobile ? "none" : "0 1px 8px rgba(0,0,0,0.04)",
            }}>
              <div key={filterKey} style={{
                opacity: feedVisible ? 1 : 0,
                transition: `opacity ${feedVisible ? "140ms" : "80ms"} ease`,
              }}>
                {!hasContent ? (
                  /* Empty state */
                  <div style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    minHeight: 360, padding: "40px 24px",
                    textAlign: "center",
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 16,
                      background: T.greenTint, border: `1.5px solid ${T.greenBorder}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: 18,
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke={T.green} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 17H20L18.6 15.6A1 1 0 0 1 18 14.8V11a6 6 0 0 0-5-5.9V5a1 1 0 0 0-2 0v.1A6 6 0 0 0 6 11v3.8a1 1 0 0 1-.6.8L4 17H9"/>
                        <path d="M13 17v1a2 2 0 0 1-4 0v-1"/>
                      </svg>
                    </div>
                    <p style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, color: T.text, margin: "0 0 6px", fontStyle: "italic" }}>
                      All caught up
                    </p>
                    <p style={{ fontSize: 14, color: T.sub, fontFamily: T.sans, margin: 0, maxWidth: 260, lineHeight: 1.65 }}>
                      {filter === "All" || filter === "Unread" ? "No new notifications. Check back after your next booking." : EMPTY_MESSAGES[filter]}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Important */}
                    {important.length > 0 && (
                      <div>
                        <GroupLabel label="Needs attention" first />
                        {important.map(n => {
                          const idx = staggerCounter++;
                          return <NotifRow key={n.id} n={n} staggerIdx={idx} navigate={navigate} onAction={handleAction} onRowClick={handleRowClick} />;
                        })}
                      </div>
                    )}

                    {/* Time groups */}
                    {sections.map((section, sIdx) => (
                      <div key={section.label}>
                        <GroupLabel label={section.label} first={important.length === 0 && sIdx === 0} />
                        {section.items.map(item => {
                          const idx = staggerCounter++;
                          if (item._isCluster) {
                            return <ClusterRow key={item.collapseKey} cluster={item} staggerIdx={idx} navigate={navigate} onAction={handleAction} onRowClick={handleRowClick} onToggle={toggleCluster} />;
                          }
                          return <NotifRow key={item.id} n={item} staggerIdx={idx} navigate={navigate} onAction={handleAction} onRowClick={handleRowClick} />;
                        })}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL — now shown on mobile too, stacked below the feed ── */}
          <div style={{ paddingTop: isMobile ? 8 : 32, flexShrink: 0, width: isMobile ? "100%" : 280 }}>
            <RightPanel navigate={navigate} />
          </div>
        </div>
      </div>

      {isMobile && <MobileBottomNav navigate={navigate} />}
    </div>
  );
}